import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import genToken from "@utils/generateToken";
import { deepCopy } from "@utils/toolBox";
import { actionSchema } from "@schemas/mCampaignAction";
import { useMCampaignStore } from "@stores/mCampaign";
import { useCCampaignsStore } from "@stores/cCampaigns";
import { useCAdsetsStore } from "@stores/cAdsets";
import { useCampaignTargetsStore } from "@stores/campaignTargets";
import { useMCampaignQuotasStore } from "@stores/mCampaignQuotas";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";

export const useMCampaignActionsStore = defineStore("mCampaignActions", () => {
  const cCampaignsStore = useCCampaignsStore();
  const { cCampaignOptions } = storeToRefs(cCampaignsStore);

  const cAdsetsStore = useCAdsetsStore();
  const { cAdsetOptions: _cAdsetOptions } = storeToRefs(cAdsetsStore);

  const campaignTargetsStore = useCampaignTargetsStore();
  const { campaignTargetOptions } = storeToRefs(campaignTargetsStore);

  const mCampaignQuotasStore = useMCampaignQuotasStore();
  const { quotaOptions } = mCampaignQuotasStore;

  const mCampaignStore = useMCampaignStore();
  const { mCampaignActions, currentMCampaign } = storeToRefs(mCampaignStore);

  const safeActions = computed(() => (currentMCampaign.value ? mCampaignActions.value : []));

  const selectedAction = ref(null);
  const selectedActionIndex = ref(null);

  const currentAction = computed(() => {
    if (selectedAction.value === null) selectAction(0);
    return selectedAction.value;
  });

  const cAdsetOptions = computed(() => {
    if (currentAction.value.c_campaigns.length > 0)
      return _cAdsetOptions.value.filter((a) => currentAction.value.c_campaigns.includes(a.c_campaign_id));
    return _cAdsetOptions.value;
  });

  const currentActionIndex = computed(() => {
    if (selectedActionIndex.value === null) selectAction(0);
    return selectedActionIndex.value;
  });

  const selectAction = (index) => {
    selectedActionIndex.value = index;
    selectedAction.value = mCampaignActions.value?.at(index);
  };

  const duplicateAction = (index) => {
    const action = deepCopy(mCampaignActions.value.at(index));
    const duplicatedProfile = { ...action, token: genToken() };
    mCampaignActions.value.splice(index + 1, 0, duplicatedProfile);
    selectAction(index + 1);
  };

  const removeAction = (index) => {
    const selected_index = selectedActionIndex.value;
    var newIndex = selected_index;

    if (index <= selected_index) {
      newIndex = selected_index !== 0 ? selected_index - 1 : 0;
    }
    mCampaignActions.value.splice(index, 1);
    selectAction(newIndex);
  };

  const addAction = () => {
    const newIndex = mCampaignActions.value.length;
    const newAction = {
      token: genToken(),
      name: "",
      active: true,
      run_only_once: false,
      trigger: "quota_progress",
      progress: 100,
      survey_target_id: campaignTargetOptions.value[0]?.value,
      action: "pause_adsets",
      c_campaigns: [],
      adsets: [],
      audiences: [],
      gender: "all",
      age_min: null,
      age_max: null,
    };
    mCampaignActions.value.splice(newIndex, 0, newAction);
    selectedAction.value = mCampaignActions.value?.at(newIndex);
    selectedActionIndex.value = newIndex;
  };

  const generateQuotaActions = async (surveyTargetId) => {
    const { response, data } = await useMyFetch(`/m_campaigns/${currentMCampaign.value.token}/preview_quota_actions`)
      .post({ survey_target_id: surveyTargetId })
      .json();
    if (!response.value.ok) {
      handleError(data);
      return null;
    }

    const { actions, warnings } = data.value;
    if (actions.length) {
      mCampaignActions.value.push(...actions);
      selectAction(mCampaignActions.value.length - actions.length);
    }
    return { added: actions.length, warnings };
  };

  const toggleActive = (index, value) => (mCampaignActions.value[index].active = value);

  const isActionActive = (index) => mCampaignActions.value[index]?.active !== false;

  const isActionSelected = (index) => mCampaignActions.value.at(index).token === currentAction.value.token;

  const noActionExist = computed(() => mCampaignActions.value.length === 0);

  const hasBeenSubmitted = ref(false);

  const actionErrors = (action) => {
    const result = actionSchema.safeParse(action);
    return result.success ? [] : result.error.issues;
  };
  const actionErrorMessages = (index) =>
    hasBeenSubmitted.value && safeActions.value[index]
      ? actionErrors(safeActions.value[index]).map((i) => i.message)
      : [];
  const areAllActionsValid = computed(() => safeActions.value.every((a) => actionSchema.safeParse(a).success));

  const validateActionsForSave = () => {
    if (areAllActionsValid.value) return true;
    hasBeenSubmitted.value = true;
    return false;
  };

  const clearActionsSubmission = () => {
    hasBeenSubmitted.value = false;
  };

  const actionTriggerOptions = [
    { value: "quota_progress", label: "Quota reached X%" },
    { value: "target_progress", label: "Target reached X%" },
    { value: "campaign_progress", label: "Survey reached X respondents" },
  ];

  const actionTypeOptions = [
    { value: "pause_campaign", label: "Pause master campaign" },
    { value: "pause_c_campaigns", label: "Pause campaigns by name" },
    { value: "pause_adsets", label: "Pause adsets by name" },
    { value: "pause_adsets_by_audience", label: "Pause adsets by audience" },
    { value: "pause_adsets_by_gender", label: "Pause adsets by gender" },
    { value: "pause_adsets_by_age_range", label: "Pause adsets by age range" },
  ];

  const adsetGenderOptions = computed(() => {
    var options = [
      { value: "all", label: "All" },
      { value: "men", label: "Men" },
      { value: "women", label: "Women" },
    ];
    return options;
  });

  const triggerLabel = (index) => {
    const trigger = mCampaignActions.value[index].trigger || "quota_progress";
    const optionLabel = actionTriggerOptions.find((option) => option.value === trigger).label;
    const progress = mCampaignActions.value[index].progress;
    return optionLabel.replace("X", progress);
  };

  const actionLabel = (index) => {
    const action = mCampaignActions.value[index].action;
    const optionslabel = actionTypeOptions.find((option) => option.value === action).label;
    return optionslabel;
  };

  return {
    mCampaignActions,
    noActionExist,
    currentAction,
    currentActionIndex,

    actionErrorMessages,
    areAllActionsValid,
    hasBeenSubmitted,
    validateActionsForSave,
    clearActionsSubmission,

    selectAction,
    duplicateAction,
    removeAction,
    addAction,
    generateQuotaActions,
    toggleActive,
    isActionSelected,
    isActionActive,

    actionTriggerOptions,
    actionTypeOptions,
    adsetGenderOptions,
    cCampaignOptions,
    cAdsetOptions,
    campaignTargetOptions,
    quotaOptions,

    triggerLabel,
    actionLabel,
  };
});
